import { FunctionComponent, memo, useEffect } from "react";
import {
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../../../Components/TextInput/TextInputFieldC";
import Select from "../../../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../../../Components/Select/ISelectProps";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { ISubjectDriveMap } from "./ISubjectDriveMap";
import { SubjectFolderMasterRead } from "../../../../Redux/Slices/Booklet/Admin/Subject_drive_map/SubjectFolderRead";
import { Subject_Drive_MapCreate_Update } from "../../../../Redux/Slices/Booklet/Admin/Subject_drive_map/SubjectFolderDriveMasterCreate_Update";

interface SubjectDriveMapFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: Partial<ISubjectDriveMap>;
  setInitialValues: React.Dispatch<React.SetStateAction<Partial<ISubjectDriveMap>>>;
  isEdit: boolean;
}

const SubjectDriveMapForm: FunctionComponent<SubjectDriveMapFormProps> = ({
  formRef,
  initialValues,
  setInitialValues,
  isEdit,
}) => {
  const dispatch = useAppDispatch();
  const { data: subjectList } = useAppSelector(
    (state) => state.SubjectFolderMasterRead
  );

  // ✅ Validation schema
  const schema = z.object({
    id: z.string().optional(),
    subject_id: z.number({ required_error: "Please select a subject" }),
    drive_folder_link: z
      .string({ required_error: "Please enter Drive Folder Link" })
      .url("Invalid Google Drive URL"),
    status: z.boolean({ required_error: "Please select status" }),
  });

  // ✅ Form setup
  const methods = useForm<ISubjectDriveMap>({
    defaultValues: {
      id: initialValues?.id ?? "",
      subject_id: initialValues?.subject_id ?? 0,
      drive_folder_link: initialValues?.drive_folder_link ?? "",
      status: initialValues?.status ?? true,
    },
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  // ✅ Fetch subjects for dropdown
  useEffect(() => {
    dispatch(SubjectFolderMasterRead());
  }, [dispatch]);

  // ✅ Reset form when editing
  useEffect(() => {
    methods.reset(initialValues);
  }, [initialValues, methods]);

  // ✅ Cleanup
  useEffect(() => {
    return () => {
      setInitialValues({});
    };
  }, [setInitialValues]);

  // ✅ Dropdown options
  const subjectOptions: AutocompleteOption[] =
    subjectList?.data?.map((s: any) => ({
      label: s.name,
      value: s.id,
    })) ?? [];

  const statusOptions: AutocompleteOption[] = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];

  // ✅ Submit logic
  const onSubmit: SubmitHandler<ISubjectDriveMap> = (values) => {
    const payload = {
      ...values,
      booklet_folder_mapping_id: values.id ?? "",
    };
    console.log("Payload =>", payload);
    dispatch(Subject_Drive_MapCreate_Update(payload));
  };

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_double_input"
      >
        {/* Subject */}
        <Select
          name="subject_id"
          label="Subject"
          options={subjectOptions}
          value={
            subjectOptions.find(
              (option) => option.value === methods.getValues("subject_id")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("subject_id", val?.value ?? 0, {
              shouldValidate: true,
            });
          }}
          required
        />

        {/* Drive Folder Link */}
        <TextInputFieldC
          name="drive_folder_link"
          label="Drive Folder Link"
          type="text"
        />

        {/* Status */}
        <Select
          name="status"
          label="Status"
          options={statusOptions}
          value={
            statusOptions.find(
              (option) => option.value === methods.getValues("status")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("status", val?.value ?? true, {
              shouldValidate: true,
            });
          }}
        />
      </form>
    </FormProvider>
  );
};

export default memo(SubjectDriveMapForm);
